/**
 * What a public flow IS: the kind of send stream it was published from.
 * One captured collection can hold several streams (a brand's onboarding
 * drip and its webinar invites land in the same inbox); each stream is
 * its own flow, titled and addressed by its kind.
 *
 * The keys are the stored contract (`newsletterSendStreams.kind`,
 * `flowTemplates.kind`); the URL segment is `FLOW_KIND_SLUGS`.
 */
export const FLOW_KINDS = [
  'onboarding',
  'trial',
  'events',
  'product_updates',
  'promotions',
] as const

export type FlowKind = (typeof FLOW_KINDS)[number]

export function isFlowKind(value: unknown): value is FlowKind {
  return (
    typeof value === 'string' &&
    (FLOW_KINDS as ReadonlyArray<string>).includes(value)
  )
}

/** The URL segment of each kind: `/browse/flows/<domain>/<segment>`. */
export const FLOW_KIND_SLUGS: Readonly<Record<FlowKind, string>> = {
  onboarding: 'onboarding',
  trial: 'trial',
  events: 'events',
  product_updates: 'product-updates',
  promotions: 'promotions',
}

/** English label for code-built strings (titles, Markdown twins, MCP lines). */
export const FLOW_KIND_LABELS: Readonly<Record<FlowKind, string>> = {
  onboarding: 'onboarding',
  trial: 'trial and upgrade',
  events: 'events and webinars',
  product_updates: 'product updates',
  promotions: 'promotions',
}

/**
 * What each kind means, as the stream classifier is told. Criteria text,
 * not UI copy: the evaluation model reads it to pick a kind.
 */
export const FLOW_KIND_CRITERIA: Readonly<Record<FlowKind, string>> = {
  onboarding:
    'Welcomes a new subscriber or user and gets them using the product: setup steps, first actions, feature tours, how-to tips, "finish your setup" or "come back" nudges, customer stories that encourage a first use. Usually the drip of a subscriber\'s first weeks.',
  trial:
    'Tied to a free trial or plan: trial start, progress and days-left reminders, expiry, upgrade or checkout nudges, win-back after the trial ends.',
  events:
    'Invites to, reminds about or follows up on a live session set for a date or time: webinars, live demos, livestreams, fireside chats, conferences, workshops, office hours, recordings of those. Stories, essays and tips are not events.',
  product_updates:
    'Announces what is new to the whole list or sends editorial content on a cadence: feature launches, changelogs, release notes, monthly newsletters, blog digests, case studies. Teaching a new user how to use the product is onboarding, not this.',
  promotions:
    'Sells with an offer: a sale, discount, coupon, limited-time deal, countdown to a deadline, seasonal campaign, bundle or upsell.',
}

/**
 * Which stream a brand's bare URL (`/browse/flows/<domain>`) opens: the
 * most "start here" kind first, the earliest stream among equals.
 */
export const FLOW_KIND_PRIORITY: ReadonlyArray<FlowKind> = [
  'onboarding',
  'trial',
  'product_updates',
  'events',
  'promotions',
]

export function flowKindRank(kind: FlowKind | null | undefined): number {
  if (!kind) {
    return FLOW_KIND_PRIORITY.length
  }
  return FLOW_KIND_PRIORITY.indexOf(kind)
}

/** "Retool onboarding flow", "Retool events and webinars flow". */
export function flowKindTitle(brandName: string, kind: FlowKind): string {
  return `${brandName} ${FLOW_KIND_LABELS[kind]} flow`
}
